import type { Item } from '@/types';

export interface PendingSyncIds {
  deletedIds: Set<string>;
  dirtyIds: Set<string>;
}

function itemsAreEqual(a: Item, b: Item): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Keep dirty/deleted tracking only while local state still differs from Drive.
 * This prevents silentRefresh() from dropping local protection before the
 * write-back has actually converged with the remote copy.
 */
export function getPendingSyncIds(
  local: Item[],
  remote: Item[],
  dirtyIds: Set<string>,
  deletedIds: Set<string>
): PendingSyncIds {
  const localMap = new Map(local.map((item) => [item.id, item]));
  const remoteMap = new Map(remote.map((item) => [item.id, item]));

  const nextDirtyIds = new Set<string>();
  for (const id of dirtyIds) {
    const localItem = localMap.get(id);
    const remoteItem = remoteMap.get(id);

    if (!localItem) continue;
    if (!remoteItem || !itemsAreEqual(localItem, remoteItem)) {
      nextDirtyIds.add(id);
    }
  }

  const nextDeletedIds = new Set<string>();
  for (const id of deletedIds) {
    if (remoteMap.has(id)) {
      nextDeletedIds.add(id);
    }
  }

  return {
    deletedIds: nextDeletedIds,
    dirtyIds: nextDirtyIds,
  };
}
